import { usePathname, useSegments } from 'expo-router';
import { useEffect, useRef, type ReactNode } from 'react';

import { trackScreen } from '../utils/analytics';

import { DatadogProvider } from './DatadogProvider';
import { PostHogClientProvider } from './PostHogClientProvider';

/**
 * Unified analytics provider that wraps the app with Datadog + PostHog
 * and enables automatic screen-view tracking.
 *
 * @example
 * ```tsx
 * <AnalyticsProvider>
 *   <App />
 * </AnalyticsProvider>
 * ```
 */

const AutoCapture = () => {
  const pathname = usePathname();
  const segments = useSegments();
  const previousPathnameRef = useRef('');

  useEffect(() => {
    if (!pathname) return;

    if (previousPathnameRef.current === pathname) {
      return;
    }

    previousPathnameRef.current = pathname;

    // captureScreens is intentionally disabled on PostHogProvider — manual tracking
    // gives us richer properties (segments, path) and avoids double-tracking.
    trackScreen(pathname, {
      segments: segments.join('/'),
      path: pathname,
    });

    // Both usePathname and useSegments derive from the same useRouteInfo() state
    // via useSyncExternalStore, so they always update together in one render.
    // pathname is the sole trigger; segments is included to satisfy exhaustive-deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return null;
};

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  return (
    <DatadogProvider>
      <PostHogClientProvider>
        <AutoCapture />
        {children}
      </PostHogClientProvider>
    </DatadogProvider>
  );
};
